import { Locale } from "@/Common/Models/Localization/Locale";
import { OntarioObstetricalFileClickableTextContext } from "@/Common/Models/UI/ClickableTextContext";
import { WeightUnit } from "@/Common/Utilities/Units/Units";
import { ObstetricalFileField } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/ObstetricalFileField";
import { MedicalCalculator } from "@/Patient/Helpers/MedicalCalculator";
import { OntarioObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/Ontario/OntarioObstetricalFileForm";
import { OntarioObstetricalVisit } from "@/Patient/Models/ObstetricalFile/Ontario/OPR3/OntarioObstetricalVisit";
import { ObstetricalFileSubsequentVisitsTable } from "../../../../Common/Subcomponent/ObstetricalFileSubsequentVisitsTable";
import { WeightUnitComponent } from "../../../../Common/Subcomponent/WeightUnitComponent";
import { OntarioObstetricalFileSubsequentVisitsTableRow } from "./OntarioObstetricalFileSubsequentVisitsTableRow";

class OntarioObstetricalFileSubsequentVisitsTable extends ObstetricalFileSubsequentVisitsTable<OntarioObstetricalFileForm, OntarioObstetricalVisit> {
    protected readonly clickableContext = OntarioObstetricalFileClickableTextContext.Opr3_SubsequentVisitComment;

    protected renderTitle(): JSX.Element {
        return (
            <div className="row">
                <div className="col-md-12">
                    <p className="text-bold f16px">{this.getString("ObstetricalFile_SubsequentVisitsLabel")}</p>
                </div>
            </div>
        );
    }

    protected renderAboveTableExtra(): JSX.Element {
        const shouldDisplayWeightUnit = this.props.form.Common.PrePregnancyWeight || !this.props.readonly;

        return (
            <div className="row row-form">
                <div className="col-md-12">
                    <ObstetricalFileField
                        label="PrePregnancyWeightAbbrevLabel"
                        field={this.controlRenderer.renderTextbox({
                            value: this.props.form.Common.ConvertedPrePregnancyWeight?.toString(),
                            isNumeric: true,
                            onChange: value => this.handlePrePregnancyWeightChange(value),
                            maxLength: 8
                        })}
                        className={`inline-field ${this.props.readonly ? "" : "medium-textbox"}`}
                        unit={shouldDisplayWeightUnit && (
                            <WeightUnitComponent
                                units={[WeightUnit.Kilogram, WeightUnit.Pound]}
                                selectedUnit={this.props.form.Common.PrePregnancyWeightUnit}
                                onUnitChange={unit => this.props.mutateModelStateWithFormUpdate(s => s.Common.PrePregnancyWeightUnit = unit)}
                                readonly={this.props.readonly}
                                acceptableLocales={[Locale.en]}
                            />
                        )}
                        inlineFlexValue
                    />
                    {this.renderField({
                        label: "BMILabel",
                        field: this.controlRenderer.renderTextbox({
                            value: this.props.form.Common.PrePregnancyBodyMassIndex?.toString(),
                            isNumeric: true,
                            onChange: value => this.props.mutateModelState(s => s.Common.PrePregnancyBodyMassIndex = Number(value)),
                            maxLength: 8
                        }),
                        className: "inline-field medium-textbox"
                    })}
                </div>
            </div>
        );
    }

    protected renderTableHeader(): JSX.Element {
        return (
            <thead>
                <tr>
                    <th className="text-bold col-medium">{cacheStore.GetString("DateLabel", null, Locale.en)}</th>
                    <th className="text-bold col-mediumsmall">{cacheStore.GetString("GestationAgeWeeksDaysAbbrevLabel", null, Locale.en)}</th>
                    <th className="text-bold col-mediumsmall">
                        {cacheStore.GetString("WeightLabel", null, Locale.en)}
                        {this.renderWeightUnit(this.props.form.Metadata.subsequentVisitsTableWeightUnit)}
                    </th>
                    <th className="text-bold col-mediumsmall">{cacheStore.GetString("BloodPressureLabel", null, Locale.en)}</th>
                    <th className="text-bold col-mediumsmall">{cacheStore.GetString("UrineProteinAbbrevLabel", null, Locale.en)}</th>
                    <th className="text-bold col-mediumsmall">{cacheStore.GetString("SymphysisToFundalHeightAbbrevLabel", null, Locale.en)}</th>
                    <th className="text-bold col-mediumsmall">{cacheStore.GetString("PresentationAbbrevLabel", null, Locale.en)}</th>
                    <th className="text-bold col-mediumsmall">{cacheStore.GetString("FetalHeartRateAbbrevLabel", null, Locale.en)}</th>
                    <th className="text-bold col-mediumsmall">{cacheStore.GetString("FetalMovementAbbrevLabel", null, Locale.en)}</th>
                    <th className="text-bold">{cacheStore.GetString("CommentsLabel", null, Locale.en)}</th>
                    <th className="text-bold col-medium">{cacheStore.GetString("NextVisitLabel", null, Locale.en)}</th>
                    <th className="text-bold col-medium">{cacheStore.GetString("InitialsLabel", null, Locale.en)}</th>
                </tr>
            </thead>
        );
    }

    protected renderTableRow(visit: OntarioObstetricalVisit, index: number): JSX.Element {
        return <OntarioObstetricalFileSubsequentVisitsTableRow {...this.getTableRowProps(visit, index)} />;
    }

    private handlePrePregnancyWeightChange(value: any): void {
        this.props.mutateModelState(s => {
            s.Common.ConvertedPrePregnancyWeight = value && parseFloat(value);

            if (s.Common.PrePregnancyWeight && this.props.heightInMeters) {
                let bodyMassIndex = MedicalCalculator.calculateBodyMassIndex(s.Common.PrePregnancyWeight, this.props.heightInMeters)?.toFixed(2);
                s.Common.PrePregnancyBodyMassIndex = bodyMassIndex ? Number(bodyMassIndex) : null;
            }
        });
    }
}

export { OntarioObstetricalFileSubsequentVisitsTable };
